import React, { useMemo } from "react";

import { faStar } from "@fortawesome/duotone-regular-svg-icons/faStar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnsType } from "antd/es/table";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { compareAsc } from "date-fns/esm";
import { IntlShape } from "react-intl";

import { EntityTypeSupportingReports } from "@mapmycustomers/shared/types/entity";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { AvatarWithName } from "@mapmycustomers/ui";

import UserPreviewCard from "@app/component/UserPreviewCard";
import Report from "@app/types/Report";
import { formatDate } from "@app/util/formatters";
import getFrequencyTypeDisplayName from "@app/util/getFrequencyTypeDisplayName";
import isValidDate from "@app/util/isValidDate";
import { parseApiDate } from "@app/util/parsers";
import { getEntityTypeDisplayName } from "@app/util/ui";

import ReportListActions from "./ReportListActions";
import styles from "./ReportTable.module.scss";

const nameComparator = (a: Report, b: Report) => (a.name ?? "").localeCompare(b.name ?? "");

const updatedAtComparator = (a: Report, b: Report) =>
  compareAsc(parseApiDate(a.updatedAt), parseApiDate(b.updatedAt));

const downloadedAtComparator = (a: Report, b: Report) => {
  if (!a.lastGeneratedAt) {
    return b.lastGeneratedAt ? -1 : 0;
  } else if (!b.lastGeneratedAt) {
    return 1;
  }

  return compareAsc(parseApiDate(a.lastGeneratedAt), parseApiDate(b.lastGeneratedAt));
};

const renderDate = (dateString: string): string => {
  const date = parseApiDate(dateString);
  return isValidDate(date) ? formatDate(parseApiDate(date), "PPp") : "";
};

const useReportListColumns = (
  intl: IntlShape,
  onDownload?: (report: Report) => void,
  onEdit?: (report: Report) => void,
  onSchedule?: (report: Report) => void,
  onDelete?: (report: Report) => void,
  onClone?: (report: Report) => void,
  onToggleFavorite?: (report: Report) => void
): ColumnsType<Report> =>
  useMemo(() => {
    const onCellProperties = (report: Report) => ({
      onClick: () => onEdit?.(report),
    });

    return [
      {
        className: styles.favorite,
        dataIndex: "isStarred",
        render: (starred: boolean, report: Report) => (
          <Tooltip
            title={intl.formatMessage(
              {
                id: "reports.exports.list.column.favorite.tooltip",
                defaultMessage: "{starred, select, true {Unfavorite} other {Favorite}}",
                description: "Tooltip of a Report Favorite field",
              },
              { starred }
            )}
          >
            <FontAwesomeIcon
              className={cn(styles.starredIcon, { [styles.starred]: starred })}
              icon={faStar}
              onClick={() => onToggleFavorite?.(report)}
            />
          </Tooltip>
        ),
        title: intl.formatMessage({
          id: "reports.exports.list.column.favorite",
          defaultMessage: "Favorite",
          description: "Title of a Report Favorite field",
        }),
        width: "5%",
      },
      {
        className: styles.clickable,
        dataIndex: "name",
        onCell: onCellProperties,
        render: (ignored: any, report: Report) => (
          <div>
            <div className={styles.name}>{report.name}</div>
            {report.description && <div className={styles.description}>{report.description}</div>}
          </div>
        ),
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: nameComparator,
        title: intl.formatMessage({
          id: "reports.exports.list.column.name",
          defaultMessage: "Export Name",
          description: "Title of a Report Name field",
        }),
      },
      {
        className: styles.clickable,
        dataIndex: "tableName",
        onCell: onCellProperties,
        render: (entityType: EntityTypeSupportingReports) => (
          <Tag color="processing">
            {getEntityTypeDisplayName(intl, entityType, { lowercase: false, plural: true })}
          </Tag>
        ),
        title: intl.formatMessage({
          id: "reports.exports.list.column.category",
          defaultMessage: "Record Type",
          description: "Title of a Record Type field",
        }),
      },
      {
        className: styles.clickable,
        dataIndex: "updatedAt",
        defaultSortOrder: "descend",
        onCell: onCellProperties,
        render: renderDate,
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: updatedAtComparator,
        title: intl.formatMessage({
          id: "reports.exports.list.column.updatedAt",
          defaultMessage: "Last Updated",
          description: "Title of a Last Updated field",
        }),
      },
      {
        className: styles.clickable,
        dataIndex: "lastGeneratedAt",
        onCell: onCellProperties,
        render: (ignored: any, report: Report) => (
          <div>
            {report.lastGeneratedAt && <div>{renderDate(report.lastGeneratedAt)}</div>}
            {report.frequency && (
              <div className={styles.frequency}>
                {getFrequencyTypeDisplayName(intl, report.frequency)}
              </div>
            )}
          </div>
        ),
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: downloadedAtComparator,
        title: intl.formatMessage({
          id: "reports.exports.list.column.downloadedAt",
          defaultMessage: "Last Downloaded",
          description: "Title of a Last Downloaded field",
        }),
      },
      {
        dataIndex: "userId",
        onCell: onCellProperties,
        render: (ignored: any, report: Report) => {
          if (report.user.deletedAt) {
            return <AvatarWithName actionOnHover className={styles.user} user={report.user} />;
          }
          return (
            <UserPreviewCard userId={report.user.id}>
              <div onClick={stopEvents}>
                <AvatarWithName actionOnHover className={styles.user} user={report.user} />
              </div>
            </UserPreviewCard>
          );
        },
        title: intl.formatMessage({
          id: "reports.exports.list.column.creator",
          defaultMessage: "Creator",
          description: "Title of Creator column",
        }),
      },
      {
        render: (ignored: any, report: Report) => {
          return (
            <ReportListActions
              onClone={onClone}
              onDelete={onDelete}
              onDownload={onDownload}
              onSchedule={onSchedule}
              report={report}
            />
          );
        },
        title: intl.formatMessage({
          id: "reports.exports.list.column.actions",
          defaultMessage: "Actions",
          description: "Title of a Actions Name field",
        }),
      },
    ];
  }, [intl, onClone, onDelete, onDownload, onEdit, onSchedule, onToggleFavorite]);

export default useReportListColumns;
